'use client';

import React, { useRef, type ReactNode } from 'react';
import { clsx } from 'clsx';
import { PlusIcon } from '../icons';
import { Eyebrow } from './Eyebrow';
import { Rail } from './Rail';
import { Section } from './Section';
import { useDisclosureMotion } from './useDisclosureMotion';
import marketingStyles from './marketing.module.css';
import styles from './Faq.module.css';

export interface FaqItem {
  /**
   * The row's `details` id, the id handed to `onExpand`, and the base of the
   * answer panel's id (`${id}-answer`). Stable ids keep analytics comparable.
   */
  readonly id: string;
  readonly question: string;
  /**
   * Rendered inside the answer paragraph. The consumer keeps the plain string
   * it feeds its FAQPage JSON-LD and, where a phrase links, renders that
   * anchor itself (an inline `TextLink` with no arrow), so schema and screen
   * come from the same source.
   */
  readonly answer: ReactNode;
}

export interface FaqCard {
  /** An anchor for the card, when the page links to it. */
  id?: string;
  heading: ReactNode;
  body?: ReactNode;
  /**
   * The action control, rendered as given. The consumer owns it – the shared
   * `Button` with `useTalkToUs`, or a page's own skin with `useBookingCta` –
   * so this section imports neither analytics nor the booking modal.
   */
  cta: ReactNode;
}

export interface FaqProps {
  /** Section id. The heading is `${id}-heading`; the rows share `name={id}`. */
  id: string;
  variant?: 'split' | 'editorial' | 'homepage';
  eyebrow: string;
  heading: string;
  /** The conversation card under the heading, sticky beside the rows on desktop. */
  card: FaqCard;
  items: readonly FaqItem[];
  /**
   * Called once per intentional expansion with the row's id, whether the
   * answer-height enhancement or the native disclosure opened it. The
   * analytics call belongs to the consumer.
   */
  onExpand?: (id: string) => void;
}

/**
 * The FAQ section: eyebrow, display heading and the conversation card on the
 * left; the accordion on the right. The design is the homepage's (HAL-1545,
 * cc-11-sage) and every FAQ page renders this one section.
 *
 * Rows are native `<details name={id}>`, as in the approved artifact: the
 * shared `name` keeps one answer open without JavaScript and every answer is
 * in the initial HTML. `useDisclosureMotion` enhances this with reversible
 * height transitions and records expansion intent on the row; `onExpand` is
 * reported once per open, from the hook while it is active and from the
 * native `toggle` only while it is not.
 *
 * Tokens resolve from the page's `MarketingSurface`. A page without one wraps
 * this section in its own `MarketingSurface`; the section declares no token.
 */
export function Faq({
  id,
  eyebrow,
  heading,
  card,
  items,
  onExpand,
  variant = 'split',
}: FaqProps): React.ReactElement {
  const headingId = `${id}-heading`;
  const list = useRef<HTMLDivElement>(null);
  useDisclosureMotion(list, { onExpand });
  const handleToggle = (itemId: string) => (event: React.SyntheticEvent<HTMLDetailsElement>) => {
    if (event.currentTarget.open && event.currentTarget.dataset.expanded === undefined) {
      onExpand?.(itemId);
    }
  };

  const invitation = (
    <div className={styles.talkCard} id={card.id}>
      <h3 className={clsx(marketingStyles.display, marketingStyles.displayH4, styles.talkHeading)}>
        {card.heading}
      </h3>
      {card.body != null && (
        <p className={clsx(marketingStyles.body, styles.talkBody)}>{card.body}</p>
      )}
      {card.cta}
    </div>
  );

  return (
    <Section
      id={id}
      labelledBy={headingId}
      className={clsx(
        variant === 'editorial' && styles.editorial,
        variant === 'homepage' && styles.homepage
      )}
    >
      <Rail className={styles.grid}>
        <div className={styles.intro}>
          <div>
            <Eyebrow className={styles.eyebrowSpacing}>{eyebrow}</Eyebrow>
            <h2
              id={headingId}
              className={clsx(marketingStyles.display, marketingStyles.displayH2, styles.heading)}
            >
              {heading}
            </h2>
          </div>
          {variant !== 'editorial' && invitation}
        </div>

        <div className={styles.list} ref={list}>
          {items.map((item) => (
            <details
              key={item.id}
              id={item.id}
              name={id}
              className={styles.row}
              onToggle={handleToggle(item.id)}
            >
              <summary className={styles.summary} aria-controls={`${item.id}-answer`}>
                <span className={styles.question}>{item.question}</span>
                <span className={styles.indicator} aria-hidden="true">
                  <PlusIcon className={styles.glyph} verticalClassName={styles.vertical} />
                </span>
              </summary>
              <div id={`${item.id}-answer`} className={styles.answerPanel} data-faq-answer>
                <p className={styles.answer}>{item.answer}</p>
              </div>
            </details>
          ))}
        </div>
        {variant === 'editorial' && invitation}
      </Rail>
    </Section>
  );
}
